"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pydanticAIAdapter = void 0;
const helpers_1 = require("../helpers");
const zod_1 = require("zod");
/**
 * Pydantic AI adapter
 * Handles pydantic-ai's parts-based message format
 *
 * Pydantic AI uses parts arrays:
 * - Text: {type: "text", content: "..."}
 * - Tool calls: {type: "tool_call", id, name, arguments}
 * - Tool responses: {type: "tool_call_response", id, name, result}
 * Tools are in: metadata.attributes.model_request_parameters.function_tools
 */
// Detection schema
const PydanticAIMessagesSchema = zod_1.z.array(zod_1.z.looseObject({
    role: zod_1.z.enum(["system", "user", "assistant"]),
    parts: zod_1.z.array(zod_1.z.any()),
}));
/**
 * Extract tool calls, text, thinking, and tool responses from parts array
 */
function extractFromParts(parts) {
    const toolCalls = [];
    const toolResponses = [];
    const thinkingParts = [];
    const redactedThinkingParts = [];
    const textParts = [];
    for (const part of parts) {
        if (!part || typeof part !== "object")
            continue;
        const p = part;
        if (p.type === "text" && typeof p.content === "string") {
            textParts.push(p.content);
        }
        else if (p.type === "thinking") {
            // Handle thinking parts - content can be in 'content' or 'thinking' field
            const thinkingContent = (() => {
                if (typeof p.content === "string") {
                    return p.content;
                }
                if (typeof p.thinking === "string") {
                    return p.thinking;
                }
                return "";
            })();
            if (thinkingContent) {
                thinkingParts.push({
                    content: thinkingContent,
                    signature: typeof p.signature === "string" ? p.signature : undefined,
                });
            }
        }
        else if (p.type === "redacted_thinking") {
            // Handle redacted/encrypted thinking
            if (typeof p.data === "string") {
                redactedThinkingParts.push({ data: p.data });
            }
        }
        else if (p.type === "tool_call") {
            toolCalls.push({
                id: p.id,
                name: p.name,
                arguments: typeof p.arguments === "string"
                    ? p.arguments
                    : JSON.stringify(p.arguments ?? {}),
                type: "function",
            });
        }
        else if (p.type === "tool_call_response") {
            toolResponses.push({
                tool_call_id: p.id,
                content: typeof p.result === "string"
                    ? p.result
                    : JSON.stringify(p.result ?? ""),
            });
        }
    }
    return {
        toolCalls,
        toolResponses,
        thinkingParts,
        redactedThinkingParts,
        text: textParts.join(""),
    };
}
/**
 * Normalize pydantic-ai tool definition to standard format
 */
function normalizeToolDefinition(tool) {
    if (!tool || typeof tool !== "object")
        return {};
    const t = tool;
    const normalized = {
        name: t.name,
        description: t.description || "",
    };
    // pydantic-ai uses parameters_json_schema instead of parameters
    if (t.parameters_json_schema) {
        normalized.parameters = t.parameters_json_schema;
    }
    return normalized;
}
/**
 * Extract tool definitions from pydantic-ai metadata
 * Tools are in: metadata.attributes.model_request_parameters.function_tools
 */
function extractToolDefinitions(metadata) {
    const meta = (0, helpers_1.parseMetadata)(metadata);
    if (!meta)
        return [];
    const tools = (0, helpers_1.getNestedProperty)(meta, "attributes", "model_request_parameters", "function_tools");
    if (Array.isArray(tools)) {
        return tools.map(normalizeToolDefinition);
    }
    return [];
}
/**
 * Normalize a single pydantic-ai message
 * Returns either a single message or array of messages (for tool responses)
 */
function normalizeMessage(msg) {
    if (!msg || typeof msg !== "object")
        return {};
    const message = msg;
    // If no parts, return as-is
    if (!Array.isArray(message.parts)) {
        return (0, helpers_1.removeNullFields)(message);
    }
    const { toolCalls, toolResponses, thinkingParts, redactedThinkingParts, text, } = extractFromParts(message.parts);
    // Extract other fields (exclude role and parts)
    const { role: _role, parts: _parts, ...rest } = message;
    // Build thinking fields if present
    const thinkingFields = {};
    if (thinkingParts.length > 0) {
        thinkingFields.thinking = thinkingParts.map((t) => ({
            type: "thinking",
            content: t.content,
            ...(t.signature ? { signature: t.signature } : {}),
        }));
    }
    if (redactedThinkingParts.length > 0) {
        thinkingFields.redacted_thinking = redactedThinkingParts.map((t) => ({
            type: "redacted_thinking",
            data: t.data,
        }));
    }
    // Assistant message with tool calls
    if (message.role === "assistant" && toolCalls.length > 0) {
        return (0, helpers_1.removeNullFields)({
            role: "assistant",
            content: text || "",
            tool_calls: toolCalls,
            ...thinkingFields,
            ...rest,
        });
    }
    // User message with tool responses - split into separate tool messages
    if (message.role === "user" && toolResponses.length > 0) {
        return toolResponses.map((tr) => (0, helpers_1.removeNullFields)({
            role: "tool",
            ...tr,
        }));
    }
    // Regular text message (may include thinking for assistant messages)
    return (0, helpers_1.removeNullFields)({
        role: message.role,
        content: text || "",
        ...thinkingFields,
        ...rest,
    });
}
function normalizeMessages(data) {
    return data.flatMap((msg) => {
        const normalized = normalizeMessage(msg);
        return Array.isArray(normalized) ? normalized : [normalized];
    });
}
function preprocessData(data, ctx) {
    if (!data)
        return data;
    // Handle array of messages
    if (Array.isArray(data)) {
        const normalized = normalizeMessages(data);
        // Extract and attach tool definitions from metadata
        const tools = extractToolDefinitions(ctx.metadata);
        if (tools.length > 0) {
            return (0, helpers_1.attachToolDefinitionsToMessages)(normalized, tools);
        }
        return normalized;
    }
    // messages wrapper
    if (typeof data === "object" && "messages" in data) {
        const obj = data;
        const messages = Array.isArray(obj.messages)
            ? normalizeMessages(obj.messages)
            : obj.messages;
        const tools = (0, helpers_1.normalizeToolDefinitionsForChatMl)(obj.tools);
        if (Array.isArray(messages) && tools.length > 0) {
            return {
                ...obj,
                messages: (0, helpers_1.attachToolDefinitionsToMessages)(messages, tools),
            };
        }
        return {
            ...obj,
            messages,
        };
    }
    // Single message
    return normalizeMessage(data);
}
exports.pydanticAIAdapter = {
    id: "pydantic-ai",
    detect(ctx) {
        const meta = (0, helpers_1.parseMetadata)(ctx.metadata);
        if (ctx.framework === "pydantic-ai")
            return true;
        const scopeName = (0, helpers_1.getNestedProperty)(meta, "scope", "name");
        if (scopeName === "pydantic-ai")
            return true;
        // STRUCTURAL: Schema-based detection on metadata (check metadata first for performance)
        if (PydanticAIMessagesSchema.safeParse(ctx.metadata).success)
            return true;
        // Schema-based detection on data (slower, do last)
        if (PydanticAIMessagesSchema.safeParse(ctx.data).success)
            return true;
        return false;
    },
    preprocess(data, _kind, ctx) {
        return preprocessData(data, ctx);
    },
};
